import React, { useState, useEffect, useCallback, Fragment } from 'react';
import axios from 'axios';
import { LoadingContainer, ButtonSpinner, InlineSpinner } from './ui/Spinner';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { formatCurrency } from '../utils/formatting';

function EditTransactionModal({ isOpen, onClose, transaction, onSave }) {
    const [form, setForm] = useState({
        amount: Math.abs(transaction?.amount || 0),
        date: transaction?.date.toISOString().split('T')[0],
        notes: transaction?.notes || ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const endpoint = transaction.type === 'withdrawal' 
                ? `/api/withdrawals/${transaction.originalId}/`
                : `/api/bankroll/${transaction.originalId}/`;

            const data = {
                amount: form.amount,
                date: form.date,
                notes: form.notes
            };

            if (transaction.type === 'deposit') {
                data.deposit_amount = form.amount;
                delete data.amount;
            }

            await axios.patch(endpoint, data);
            onSave();
            onClose();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update transaction');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-[#faf8f5] dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                                <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Edit {transaction?.type.charAt(0).toUpperCase() + transaction?.type.slice(1)}
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Amount
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={form.amount}
                                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                            className="block w-full rounded-lg border border-gray-200 dark:border-gray-700 
                                                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Date
                                        </label>
                                        <input
                                            type="date"
                                            value={form.date}
                                            onChange={(e) => setForm({ ...form, date: e.target.value })}
                                            className="block w-full rounded-lg border border-gray-200 dark:border-gray-700 
                                                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Notes
                                        </label>
                                        <textarea
                                            value={form.notes}
                                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                            className="block w-full rounded-lg border border-gray-200 dark:border-gray-700 
                                                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                            rows={3}
                                        />
                                    </div>

                                    {error && (
                                        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm">
                                            {error}
                                        </div>
                                    )}

                                    <div className="mt-6 flex justify-end gap-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                                                     hover:text-gray-900 dark:hover:text-white"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 
                                                     rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 
                                                     focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
                                        >
                                            {loading ? <ButtonSpinner /> : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}

function TransactionHistory() {
    const [transactions, setTransactions] = useState([]);
    const [monthlySummary, setMonthlySummary] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [editingTransaction, setEditingTransaction] = useState(null);

    const calculateMonthlySummary = useCallback((transactions) => {
        const monthlyMap = {};

        transactions.forEach(transaction => {
            const monthKey = `${transaction.date.getFullYear()}-${String(transaction.date.getMonth() + 1).padStart(2, '0')}`;

            if (!monthlyMap[monthKey]) {
                monthlyMap[monthKey] = {
                    month: monthKey,
                    deposits: 0,
                    withdrawals: 0,
                    net: 0
                };
            }

            if (transaction.type === 'deposit') {
                monthlyMap[monthKey].deposits += transaction.amount;
            } else if (transaction.type === 'withdrawal') {
                monthlyMap[monthKey].withdrawals += Math.abs(transaction.amount);
            }
        });

        // Calculate net and convert to array, sorted by month descending
        return Object.values(monthlyMap)
            .map(month => ({
                ...month,
                net: month.deposits - month.withdrawals
            }))
            .sort((a, b) => b.month.localeCompare(a.month));
    }, []);

    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        try {
            const [depositsRes, withdrawalsRes] = await Promise.all([
                axios.get('/api/deposits/'),
                axios.get('/api/withdrawals/')
            ]);

            const depositsData = depositsRes.data?.results || depositsRes.data || [];
            const withdrawalsData = withdrawalsRes.data?.results || withdrawalsRes.data || [];

            const allTransactions = [
                ...depositsData.map(d => ({
                    id: `d-${d.id}`,
                    originalId: d.id,
                    date: new Date(d.date),
                    type: 'deposit',
                    amount: parseFloat(d.amount),
                    notes: d.reason
                })),
                ...withdrawalsData.map(w => ({
                    id: `w-${w.id}`,
                    originalId: w.id,
                    date: new Date(w.date),
                    type: 'withdrawal',
                    amount: -parseFloat(w.amount),
                    notes: w.reason
                }))
            ].sort((a, b) => b.date - a.date);

            setTransactions(allTransactions);
            setMonthlySummary(calculateMonthlySummary(allTransactions));
        } catch (err) {
            setError('Failed to load transaction history');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [calculateMonthlySummary]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions]);

    const handleDelete = async (transaction) => {
        if (!window.confirm('Are you sure you want to delete this transaction?')) {
            return;
        }

        setDeleteLoading(transaction.id);
        try {
            if (transaction.type === 'withdrawal') {
                await axios.delete(`/api/withdrawals/${transaction.originalId}/`);
            } else if (transaction.type === 'deposit') {
                await axios.delete(`/api/deposits/${transaction.originalId}/`);
            } else {
                await axios.delete(`/api/bankroll/${transaction.originalId}/`);
            }
            await fetchTransactions(); // Refresh the list
        } catch (err) {
            setError('Failed to delete transaction');
            console.error(err);
        } finally {
            setDeleteLoading(null);
        }
    };

    if (loading) {
        return <LoadingContainer />;
    }

    if (error) {
        return (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                {error}
            </div>
        );
    }

    const formatMonthYear = (monthKey) => {
        const [year, month] = monthKey.split('-');
        const date = new Date(year, parseInt(month) - 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    };

    return (
        <div className="space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Transaction History</h2>

            {/* Monthly Summary */}
            {monthlySummary.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Monthly Summary</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900/50">
                                <tr>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Month
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Deposits
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Withdrawals
                                    </th>
                                    <th scope="col" className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Net
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {monthlySummary.map(month => (
                                    <tr key={month.month} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                        <td className="px-3 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                                            {formatMonthYear(month.month)}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-right text-green-600 dark:text-green-400 font-medium whitespace-nowrap">
                                            {formatCurrency(month.deposits)}
                                        </td>
                                        <td className="px-3 py-2 text-sm text-right text-red-600 dark:text-red-400 font-medium whitespace-nowrap">
                                            {formatCurrency(month.withdrawals)}
                                        </td>
                                        <td className={`px-3 py-2 text-sm text-right font-medium whitespace-nowrap ${
                                            month.net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                        }`}>
                                            {month.net >= 0 ? '+' : ''}{formatCurrency(month.net)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-100 dark:bg-gray-900">
                                {(() => {
                                    const totalDeposits = monthlySummary.reduce((sum, m) => sum + m.deposits, 0);
                                    const totalWithdrawals = monthlySummary.reduce((sum, m) => sum + m.withdrawals, 0);
                                    const totalNet = totalDeposits - totalWithdrawals;
                                    return (
                                        <tr className="font-semibold">
                                            <td className="px-3 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                                                Total
                                            </td>
                                            <td className="px-3 py-2 text-sm text-right text-green-600 dark:text-green-400 whitespace-nowrap">
                                                {formatCurrency(totalDeposits)}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-right text-red-600 dark:text-red-400 whitespace-nowrap">
                                                {formatCurrency(totalWithdrawals)}
                                            </td>
                                            <td className={`px-3 py-2 text-sm text-right whitespace-nowrap ${
                                                totalNet >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                            }`}>
                                                {totalNet >= 0 ? '+' : ''}{formatCurrency(totalNet)}
                                            </td>
                                        </tr>
                                    );
                                })()}
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Type
                                </th>
                                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Amount
                                </th>
                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Notes
                                </th>
                                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {transactions.map(transaction => (
                                <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                    <td className="px-3 py-4 text-sm text-gray-900 dark:text-white">
                                        {transaction.date.toLocaleDateString()}
                                    </td>
                                    <td className="px-3 py-4 text-sm">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                            ${transaction.type === 'initialization' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                                              transaction.type === 'deposit' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                                              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>
                                            {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                                        </span>
                                    </td>
                                    <td className={`px-3 py-4 text-sm text-right font-medium
                                        ${transaction.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                        {formatCurrency(Math.abs(transaction.amount))}
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                                        {transaction.notes || '-'}
                                    </td>
                                    <td className="px-3 py-4 text-sm text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => setEditingTransaction(transaction)}
                                                disabled={transaction.type === 'initialization'}
                                                className={`text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400
                                                    ${transaction.type === 'initialization' ? 'opacity-50 cursor-not-allowed' : ''}
                                                    transition-colors duration-200`}
                                                title={transaction.type === 'initialization' ? "Can't edit initial bankroll" : "Edit transaction"}
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(transaction)}
                                                disabled={deleteLoading === transaction.id || transaction.type === 'initialization'}
                                                className={`text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400
                                                    ${transaction.type === 'initialization' ? 'opacity-50 cursor-not-allowed' : ''}
                                                    transition-colors duration-200`}
                                                title={transaction.type === 'initialization' ? "Can't delete initial bankroll" : "Delete transaction"}
                                            >
                                                {deleteLoading === transaction.id ? (
                                                    <InlineSpinner color="red" />
                                                ) : (
                                                    <TrashIcon className="h-5 w-5" />
                                                )}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-gray-100 dark:bg-gray-900">
                            {(() => {
                                const totalDeposits = transactions.filter(t => t.type === 'deposit').reduce((sum, t) => sum + t.amount, 0);
                                const totalWithdrawals = transactions.filter(t => t.type === 'withdrawal').reduce((sum, t) => sum + Math.abs(t.amount), 0);
                                return (
                                    <tr className="font-semibold">
                                        <td colSpan="2" className="px-3 py-3 text-sm text-gray-900 dark:text-white">
                                            Total
                                        </td>
                                        <td className="px-3 py-3 text-sm text-right whitespace-nowrap">
                                            <span className="text-green-600 dark:text-green-400">{formatCurrency(totalDeposits)}</span>
                                            <span className="text-gray-400 mx-1">/</span>
                                            <span className="text-red-600 dark:text-red-400">{formatCurrency(totalWithdrawals)}</span>
                                        </td>
                                        <td colSpan="2"></td>
                                    </tr>
                                );
                            })()}
                        </tfoot>
                    </table>
                </div>
            </div>

            {editingTransaction && (
                <EditTransactionModal
                    isOpen={!!editingTransaction}
                    onClose={() => setEditingTransaction(null)}
                    transaction={editingTransaction}
                    onSave={fetchTransactions}
                />
            )}
        </div>
    );
}

export default TransactionHistory; 